import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Grid } from '@mui/material_modern'
import { useGetDispatchTicketsQuery } from 'services/dispatch'
import { useGetDriversQuery } from 'services/drivers'
import DispatchTicketHistoryGrid from 'components/generic/datagrid/DispatchTicketHistoryGrid'
import useGlobalLoader from 'utils/custom-hooks/useGlobalLoader'
import {
  DISPATCH_TICKET_TYPES,
  DISPATCH_TICKET_STATUSES,
  FILTER_OPTION_TYPES,
  PAGE_SIZE,
  DT_STATUS_ENUM,
} from 'utils/constants'
import { capAllWords, objectDetailsToDropdownFormatter } from 'utils/data-manipulation/common'

const DispatchTicketHistoryContainer = () => {
  const { inOrgMode } = useSelector(state => state.ui)
  const { yardDetailsList, user } = useSelector(state => state.auth)

  const defaultFilter = {
    search: '',
    driverIds: null,
    types: null,
    statuses: [
      DISPATCH_TICKET_STATUSES.UNASSIGNED.id,
      DISPATCH_TICKET_STATUSES.ASSIGNED.id,
      DISPATCH_TICKET_STATUSES.IN_PROGRESS.id,
      DISPATCH_TICKET_STATUSES.COMPLETED.id,
      DISPATCH_TICKET_STATUSES.FAILED.id,
    ],
    yardIds: null,
  }

  const filterStorageString = useMemo(() => {
    if (!user?.attributes?.sub) return
    return 'dispatchTicketHistoryFilter:' + user?.attributes?.sub
  }, [user])

  const storedFilter = useMemo(() => {
    if (!filterStorageString) return

    const storedFilterString = window.localStorage.getItem(filterStorageString)
    return storedFilterString && storedFilterString !== 'undefined' ? JSON.parse(storedFilterString) : {}
  }, [filterStorageString])

  const startingFilter = useMemo(
    () => ({
      ...defaultFilter,
      driverIds: storedFilter?.driverIds || defaultFilter.driverIds,
      types: storedFilter?.types || defaultFilter.types,
      statuses: storedFilter?.statuses === null ? defaultFilter.statuses : storedFilter?.statuses,
      yardIds: inOrgMode ? storedFilter?.yardIds || defaultFilter.yardIds : null,
    }),
    [storedFilter, inOrgMode],
  )

  const onReset = () => {
    window.localStorage.setItem(filterStorageString, JSON.stringify(defaultFilter))
  }

  const [paginationOptions, setPaginationOptions] = useState({ page: 1, pageSize: PAGE_SIZE })
  const [filter, setFilter] = useState(startingFilter)

  const { data: ticketHistoryData, isFetching: isFetchingTicketHistoryData } = useGetDispatchTicketsQuery(
    {
      filter: JSON.stringify({
        ...filter,
        statuses: filter.statuses ? filter.statuses : defaultFilter.statuses, // We always explicitly send the filter so that REQUESTED is not included
      }),
      ...paginationOptions,
    },
    { refetchOnMountOrArgChange: true },
  )

  const { data: driversData, isFetching: isFetchingDriversData } = useGetDriversQuery({
    filter: JSON.stringify({ isActive: true }),
  })

  useGlobalLoader(false)

  const additionalFilterOptions = [
    {
      type: FILTER_OPTION_TYPES.MULTI_SELECT,
      name: 'driverIds',
      label: 'Driver',
      options: driversData?.Items || [],
      defaultValue: startingFilter?.driverIds,
      width: '200px',
      onChange: value => {
        const newFilter = {
          ...filter,
          driverIds: value,
        }
        window.localStorage.setItem(filterStorageString, JSON.stringify(newFilter))
      },
    },
    {
      type: FILTER_OPTION_TYPES.MULTI_SELECT,
      name: 'types',
      label: 'Ticket Type',
      options: [
        DISPATCH_TICKET_TYPES.PICK_UP,
        DISPATCH_TICKET_TYPES.DROP_OFF,
        DISPATCH_TICKET_TYPES.EXCHANGE,
        DISPATCH_TICKET_TYPES.PICK_AND_RETURN,
        DISPATCH_TICKET_TYPES.DELIVERY,
        DISPATCH_TICKET_TYPES.LIVE_LOAD,
      ],
      defaultValue: startingFilter?.types,
      width: '200px',
      onChange: value => {
        const newFilter = {
          ...filter,
          types: value,
        }
        window.localStorage.setItem(filterStorageString, JSON.stringify(newFilter))
      },
    },
    {
      type: FILTER_OPTION_TYPES.MULTI_SELECT,
      name: 'statuses',
      label: 'Ticket Status',
      options: [
        DISPATCH_TICKET_STATUSES.ASSIGNED,
        DISPATCH_TICKET_STATUSES.UNASSIGNED,
        DISPATCH_TICKET_STATUSES.IN_PROGRESS,
        DISPATCH_TICKET_STATUSES.FAILED,
        DISPATCH_TICKET_STATUSES.COMPLETED,
        DISPATCH_TICKET_STATUSES.VOID,
      ],
      defaultValue: startingFilter?.statuses,
      width: '200px',
      onChange: value => {
        const newFilter = {
          ...filter,
          statuses: value,
        }
        window.localStorage.setItem(filterStorageString, JSON.stringify(newFilter))
      },
    },
    {
      type: FILTER_OPTION_TYPES.DATE,
      fromDateName: 'scheduleDateFrom',
      toDateName: 'scheduleDateTo',
      label: 'Date Scheduled',
      width: '200px',
    },
  ]

  // If in org mode, we add in the option to filter by yard
  if (inOrgMode) {
    additionalFilterOptions.unshift({
      type: FILTER_OPTION_TYPES.MULTI_SELECT,
      name: 'yardIds',
      label: 'Yards',
      options: yardDetailsList?.map((item, index) => objectDetailsToDropdownFormatter(item, index)) || [],
      defaultValue: startingFilter?.yardIds,
      width: '200px',
      onChange: value => {
        const newFilter = {
          ...filter,
          yardIds: value,
        }
        window.localStorage.setItem(filterStorageString, JSON.stringify(newFilter))
      },
    })
  }

  const transformedRows = (ticketHistoryData?.Items || []).slice().map(ticketHistory => ({
    id: ticketHistory.id,
    externalId: ticketHistory.externalId,
    status: DISPATCH_TICKET_STATUSES[ticketHistory.status]?.id,
    statusName: DISPATCH_TICKET_STATUSES[ticketHistory.status]?.name,
    type: DISPATCH_TICKET_TYPES[ticketHistory.type]?.name,
    yard: ticketHistory.yard?.name ? capAllWords(ticketHistory.yard.name) : '',
    driver: ticketHistory.driver?.name || '---',
    customer: ticketHistory.customer?.displayName,
    location: ticketHistory.location?.name,
    dateCreated: ticketHistory.requestDate,
    dateScheduled: ticketHistory.scheduleDate,
    recurringDispatchTicketId: ticketHistory.recurringDispatchTicketId,
  }))

  return (
    <Grid p={3} sx={{ height: '100%' }} container flexDirection="column">
      <DispatchTicketHistoryGrid
        rows={transformedRows}
        loading={isFetchingTicketHistoryData || isFetchingDriversData}
        useBackendSortAndFilter
        filter={filter}
        setFilter={setFilter}
        paginationMode="server"
        rowCount={ticketHistoryData?.totalResultsCount || 0}
        onPageChange={newPageNumber => setPaginationOptions(prevState => ({ ...prevState, page: newPageNumber + 1 }))}
        onPageSizeChange={newPageSize => setPaginationOptions(prevState => ({ ...prevState, pageSize: newPageSize }))}
        pageSize={paginationOptions.pageSize}
        additionalFilterOptions={additionalFilterOptions}
        onReset={onReset}
        defaultFilter={defaultFilter}
      />
    </Grid>
  )
}

export default DispatchTicketHistoryContainer
